import { IComboBoxOption } from '@fluentui/react';
import { IVerticalStackedChartProps, IVSChartDataPoint, VerticalStackedBarChart } from '@fluentui/react-charting';
import AppContext from 'App/AppContext';
import { IPDropdown, IPLabel, IPSelectableOption, IPSpinner, IPText } from 'baseComponents';
import { IPComboBox } from 'baseComponents/Combobox/IPComboBox';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import TenantPicker, { PickerTenant, TenantPickerMode } from 'components/Pickers/TenantPicker';
import { globalStackTokensGapMedium } from 'globalStyles';
import { TFunction } from 'i18next';
import Control, { ApplicabilityReasons, ApplicabilityReasonsColor, ControlScope, ScopeColor } from 'models/control';
import { NormCoverage } from 'models/dashboardScene';
import { EntityStatus, EntityStatusColor } from 'models/entityStatus';
import Norm, { Standards } from 'models/norm';
import { isPartnerWidgetType, WidgetSizes, WidgetTenantConfig, WidgetWidth } from 'models/widget';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetWidgetDataTenantControlStatus } from 'services/Api/dashboardService';
import { apiRequest } from 'services/Auth/authConfig';
import { enumParseKey } from 'utils/object';
import { sortOnString } from 'utils/sorting';
import { IWidgetConfigRendererProps } from '../WidgetConfigRenderer';
import { IWidgetRendererProps } from '../WidgetRenderer';
import { apiGetTenantsForPartner } from 'services/Api/partnerService';
import {
  getConfiguredTenantIds,
  getISONormsForTenants,
  getTenantWidgetTenantById,
  getTenantWidgetTenantId,
  getTenantWidgetTenantName,
  sortTenantWidgetTenantsByName,
  TenantWidgetTenant,
  WithTenantId,
  withTenantId,
} from './tenantWidgetData';
import { useTenantDrillDown } from './useTenantDrillDown';

export enum WidgetTenantControlStatusProperties {
  ControlStatus = 0,
  ApplicabilityReason = 1,
  LinkedImplementation = 2,
}

export const getPropertyText = (
  property: WidgetTenantControlStatusProperties | undefined,
  t: TFunction<string[]>,
): string => {
  switch (property) {
    case WidgetTenantControlStatusProperties.ApplicabilityReason:
      return t('widgets:TenantControlStatus.Config.Properties.ApplicabilityReason');
    case WidgetTenantControlStatusProperties.ControlStatus:
      return t('widgets:TenantControlStatus.Config.Properties.ControlStatus');
    case WidgetTenantControlStatusProperties.LinkedImplementation:
      return t('widgets:TenantControlStatus.Config.Properties.LinkedImplementation');
    default:
      return t('widgets:TenantControlStatus.Config.Properties.ControlStatus');
  }
};

export class WidgetTenantControlStatusConfig extends WidgetTenantConfig {
  isoNormId?: number;

  property?: WidgetTenantControlStatusProperties;

  constructor() {
    super();
  }

  load(raw: string | undefined) {
    if (raw) {
      try {
        const newRawConfig = JSON.parse(raw);
        this.loadTenant(newRawConfig);
        this.isoNormId = newRawConfig.isoNormId ?? undefined;
        this.property = newRawConfig.property ?? WidgetTenantControlStatusProperties.ControlStatus;
      } catch {
        //ignore
      }
    }
  }

  clone(): WidgetTenantControlStatusConfig {
    const newConfig = this.cloneTenant() as WidgetTenantControlStatusConfig;
    newConfig.isoNormId = this.isoNormId;
    newConfig.property = this.property;

    return newConfig;
  }
}

interface IWidgetTenantControlStatusProps extends IWidgetRendererProps {}

const WidgetTenantControlStatus = (props: IWidgetTenantControlStatusProps) => {
  const { t } = useTranslation(['translation', 'widgets', 'control', 'norms']);
  const appContext = useContext(AppContext);
  const [data, setData] = useState<NormCoverage[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [tenants, setTenants] = useState<TenantWidgetTenant[]>([]);
  const [tenantsToShow, setTenantsToShow] = useState<TenantWidgetTenant[]>([]);
  const [config, setConfig] = useState<WidgetTenantControlStatusConfig>(new WidgetTenantControlStatusConfig());
  //undefined until resolved, so the invalid-config message cannot flash before the standards are known
  const [availableISONorms, setAvailableISONorms] = useState<Norm[] | undefined>(undefined);

  useEffect(() => {
    const loadData = async () => {
      try {
        if (isLoading) return;
        setIsLoading(true);

        await appContext.globalDataCache.tags.getItems();
        const config = loadConfig();
        setConfig(config);

        const isPartnerWidget = isPartnerWidgetType(props.widget.widgetType);
        const tenants: TenantWidgetTenant[] = isPartnerWidget
          ? props.sharedData?.partnerCustomers ?? []
          : props.sharedData?.userOrgUnits ?? [];
        setTenants(tenants);
        const tenantIds = getConfiguredTenantIds(config.tenants, tenants);
        if (tenantIds.length === 0) {
          setData([]);
          setTenantsToShow([]);
          setAvailableISONorms([]);

          return;
        }

        //A partner widget describes its customers, so the standards it can show are the customers' standards.
        //globalDataCache.norms holds the signed-in tenant's standards, which on a partner dashboard is the partner.
        const isoNorms = isPartnerWidget
          ? getISONormsForTenants(tenants, tenantIds)
          : appContext.globalDataCache.norms.getItemsISO();
        setAvailableISONorms(isoNorms);

        let isoNormId = config.isoNormId;
        if (!isoNormId || !isoNorms.some((norm) => norm.isoNormId === isoNormId)) {
          //Never substitute a different standard on a partner widget: rendering another standard's coverage under
          //the configured standard's name presents wrong data as if it were right. Say nothing instead.
          isoNormId = isPartnerWidget ? undefined : getDefaultISONormId();
        }

        if (!isoNormId) {
          setData([]);
          setTenantsToShow([]);

          return;
        }

        config.isoNormId = isoNormId;
        setConfig(config);

        const accessToken = await appContext.getAccessToken(apiRequest.scopes);

        const data =
          (await apiGetWidgetDataTenantControlStatus(
            tenantIds,
            config.isoNormId ?? 0,
            accessToken,
            props.abortSignal,
          )) ?? [];

        setData(data);

        const tenantsToShow = tenants.filter((tenant) =>
          data.some((s) => s.tenantId === getTenantWidgetTenantId(tenant)),
        );
        sortTenantWidgetTenantsByName(tenantsToShow);
        setTenantsToShow(tenantsToShow);
      } catch (err) {
        appContext.setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget, props.sharedData?.partnerCustomers, props.sharedData?.userOrgUnits]);

  const loadConfig = (): WidgetTenantControlStatusConfig => {
    const newConfig = new WidgetTenantControlStatusConfig();
    newConfig.load(props.widget.widgetConfig);

    return newConfig;
  };

  const getDefaultISONormId = (): number | undefined => {
    const isoNorms = appContext.globalDataCache.norms.getItemsISO().sort((a, b) => sortOnString(a.name, b.name));
    const iso27001 = isoNorms.find((norm) => norm.isoNormId === Standards.ISO27001_2022);

    if (iso27001) {
      return iso27001.isoNormId;
    }

    return isoNorms[0]?.isoNormId;
  };

  const getData = (): IVerticalStackedChartProps[] => {
    if (data.length === 0 || tenantsToShow.length === 0) return [];

    switch (config.property) {
      case WidgetTenantControlStatusProperties.ApplicabilityReason:
        return getDataByApplicabilityReason();
      case WidgetTenantControlStatusProperties.ControlStatus:
        return getDataByControlStatus();
      case WidgetTenantControlStatusProperties.LinkedImplementation:
        return getDataByControlScope();
      default:
        return getDataByControlStatus();
    }
  };

  const getDataByApplicabilityReason = (): IVerticalStackedChartProps[] => {
    const graphData: WithTenantId<IVerticalStackedChartProps>[] = data.map((normCoverage) => {
      const tenantId = normCoverage.tenantId ?? '';
      const tenant = tenantsToShow.find((t) => tenantId === getTenantWidgetTenantId(t));
      const tenantName = getTenantWidgetTenantName(tenant);
      const dataPoints: IVSChartDataPoint[] = [];

      if (normCoverage.applicabilityCount) {
        for (const [key, value] of Object.entries(normCoverage.applicabilityCount)) {
          const reason = enumParseKey(ApplicabilityReasons, key);
          if (reason === undefined) continue;
          dataPoints.push({
            legend: Control.getApplicabilityReasonText([reason], t),
            data: value,
            color: ApplicabilityReasonsColor[reason],
            xAxisCalloutData: tenantName,
          });
        }
      }

      return {
        chartData: withTenantId(dataPoints, tenantId),
        xAxisPoint: tenantName,
        tenantId,
      };
    });

    return graphData;
  };

  const getDataByControlStatus = (): IVerticalStackedChartProps[] => {
    const graphData: WithTenantId<IVerticalStackedChartProps>[] = data.map((normCoverage) => {
      const tenantId = normCoverage.tenantId ?? '';
      const tenant = tenantsToShow.find((t) => tenantId === getTenantWidgetTenantId(t));
      const tenantName = getTenantWidgetTenantName(tenant);

      const dataPoints: IVSChartDataPoint[] = [
        {
          legend: Control.getApplicabilityReasonText([ApplicabilityReasons.OutOfScope], t),
          data: normCoverage.outOfScopeCount,
          color: ApplicabilityReasonsColor[ApplicabilityReasons.OutOfScope],
          xAxisCalloutData: tenantName,
        },
        {
          legend: Control.getControlStateText(EntityStatus.Todo, t),
          data: normCoverage.toDoCount,
          color: EntityStatusColor[EntityStatus.Todo],
          xAxisCalloutData: tenantName,
        },
        {
          legend: Control.getControlStateText(EntityStatus.Designing, t),
          data: normCoverage.designingCount,
          color: EntityStatusColor[EntityStatus.Designing],
          xAxisCalloutData: tenantName,
        },
        {
          legend: Control.getControlStateText(EntityStatus.Implementing, t),
          data: normCoverage.implementingCount,
          color: EntityStatusColor[EntityStatus.Implementing],
          xAxisCalloutData: tenantName,
        },
        {
          legend: Control.getControlStateText(EntityStatus.Implemented, t),
          data: normCoverage.implementedCount,
          color: EntityStatusColor[EntityStatus.Implemented],
          xAxisCalloutData: tenantName,
        },
      ];

      return {
        chartData: withTenantId(dataPoints, tenantId),
        xAxisPoint: tenantName,
        tenantId,
      };
    });

    return graphData;
  };

  const getDataByControlScope = (): IVerticalStackedChartProps[] => {
    const graphData: WithTenantId<IVerticalStackedChartProps>[] = data.map((normCoverage) => {
      const tenantId = normCoverage.tenantId ?? '';
      const tenant = tenantsToShow.find((t) => tenantId === getTenantWidgetTenantId(t));
      const tenantName = getTenantWidgetTenantName(tenant);

      const dataPoints: IVSChartDataPoint[] = [
        {
          legend: Control.getScopeText(ControlScope.Covered, t),
          data: normCoverage.controlCount - normCoverage.controlLinkedCount,
          color: ScopeColor[ControlScope.Covered],
          xAxisCalloutData: tenantName,
        },
        {
          legend: Control.getScopeText(ControlScope.Linked, t),
          data: normCoverage.controlLinkedCount,
          color: ScopeColor[ControlScope.Linked],
          xAxisCalloutData: tenantName,
        },
        {
          legend: Control.getScopeText(ControlScope.NotCovered, t),
          data: normCoverage.noControlCount,
          color: ScopeColor[ControlScope.NotCovered],
          xAxisCalloutData: tenantName,
        },
      ];

      return {
        chartData: withTenantId(dataPoints, tenantId),
        xAxisPoint: tenantName,
        tenantId,
      };
    });

    return graphData;
  };

  const getFilterForProperty = (
    property: WidgetTenantControlStatusProperties | undefined,
    data: IVSChartDataPoint,
  ): string => {
    switch (property) {
      case WidgetTenantControlStatusProperties.ApplicabilityReason: {
        const reasonEntry = Object.entries(ApplicabilityReasonsColor).find(([_, color]) => color === data.color);
        if (!reasonEntry) return '';

        return `applicability=${reasonEntry[0]}`;
      }
      case WidgetTenantControlStatusProperties.ControlStatus: {
        const statusEntry = Object.entries(EntityStatusColor).find(([_, color]) => color === data.color);
        if (!statusEntry) return '';

        return `state=${statusEntry[0]}`;
      }
      case WidgetTenantControlStatusProperties.LinkedImplementation: {
        const scopeEntry = Object.entries(ScopeColor).find(([_, color]) => color === data.color);
        if (!scopeEntry) return '';

        let filterValue = '';
        switch (Number(scopeEntry[0])) {
          case ControlScope.Covered:
            filterValue = 'covered';
            break;
          case ControlScope.Linked:
            filterValue = 'linked';
            break;
          case ControlScope.NotCovered:
            filterValue = 'noControl';
            break;
          default:
            return '';
        }

        return `scope=${filterValue}`;
      }
      default:
        return '';
    }
  };

  const drillDown = useTenantDrillDown();

  const onClick = (data: IVSChartDataPoint) => {
    const tenant = getTenantWidgetTenantById(tenants, (data as WithTenantId<IVSChartDataPoint>).tenantId);
    if (!tenant) return;
    const filter = getFilterForProperty(config.property, data);
    let url = `/standards/overview?isonorm=${config.isoNormId}`;
    if (filter) {
      url += `&${filter}`;
    }
    drillDown(tenant, url, isPartnerWidgetType(props.widget.widgetType));
  };

  //
  // Main render
  //
  if (!data || isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  if (
    !isLoading &&
    availableISONorms !== undefined &&
    (!config.isoNormId || !availableISONorms.some((norm) => norm.isoNormId === config.isoNormId))
  ) {
    //config is invalid: the configured standard is not run by any of the selected tenants
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPText>{t('widgets:TenantControlStatus.Config.Invalid')}</IPText>
      </IPStack>
    );
  }

  if (!isLoading && (data.length === 0 || tenantsToShow.length === 0)) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPText>{t('translation:General.Notifications.NoItemsFilter')}</IPText>
      </IPStack>
    );
  }

  const shouldRotateXAxisLabels = tenantsToShow.length > 10 && props.widget.widgetSize !== WidgetSizes.Large;

  return (
    <IPStack verticalFill>
      <IPStack.Item grow>
        <VerticalStackedBarChart
          data={getData()}
          height={250}
          width={WidgetWidth[props.widget.widgetSize] - 80}
          wrapXAxisLables={!shouldRotateXAxisLabels}
          rotateXAxisLables={shouldRotateXAxisLabels}
          barWidth={'auto'}
          legendProps={{
            centerLegends: true,
            enabledWrapLines: true,
          }}
          onBarClick={(ev, data) => onClick(data as IVSChartDataPoint)}
          lineOptions={{
            lineBorderWidth: 1,
          }}
        />
      </IPStack.Item>
    </IPStack>
  );
};

export default WidgetTenantControlStatus;

//
// Config
//
interface IWidgetConfigWidgetTenantControlStatusProps extends IWidgetConfigRendererProps {}

export const WidgetConfigTenantControlStatus = (props: IWidgetConfigWidgetTenantControlStatusProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation', 'widgets']);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isoNorms, setISONorms] = useState<Norm[]>([]);
  const [selectedTenants, setSelectedTenants] = useState<string[]>([]);
  //kept so the standard list can be re-derived when the tenant selection changes
  const [partnerCustomers, setPartnerCustomers] = useState<TenantWidgetTenant[]>([]);
  const [norm, setNorm] = useState<Norm | undefined>(undefined);
  const [property, setProperty] = useState<WidgetTenantControlStatusProperties>(
    WidgetTenantControlStatusProperties.ControlStatus,
  );
  const [currentWidgetId, setCurrentWidgetId] = useState<number>(-1);

  useEffect(() => {
    if (currentWidgetId === -1 || currentWidgetId !== props.widget.widgetId) {
      setCurrentWidgetId(props.widget.widgetId);
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget]);

  const loadConfig = (): WidgetTenantControlStatusConfig => {
    let newRawConfig = new WidgetTenantControlStatusConfig();
    if (props.widget.widgetConfig) {
      newRawConfig = JSON.parse(props.widget.widgetConfig);
    }

    return newRawConfig;
  };

  const updateTenants = (items: PickerTenant[]) => {
    const tenants = items.map((i) => i.tenantId);
    setSelectedTenants(tenants);

    //the offered standards follow the selection, so a standard nobody runs anymore drops out of the list
    let selectedNorm = norm;
    if (isPartnerWidgetType(props.widget.widgetType)) {
      const newISONorms = getISONormsForTenants(partnerCustomers, tenants).sort((a, b) => sortOnString(a.name, b.name));
      setISONorms(newISONorms);

      selectedNorm = newISONorms.find((n) => n.isoNormId === norm?.isoNormId);
      setNorm(selectedNorm);
    }

    const config = getConfig(tenants, selectedNorm?.isoNormId, property);
    props.onUpdateConfig(JSON.stringify(config), selectedNorm !== undefined);
  };

  const updateNorm = (isoNormId: number) => {
    const norm = isoNorms.find((n) => n.isoNormId === isoNormId);
    setNorm(norm);
    const config = getConfig(selectedTenants, isoNormId, property);
    props.onUpdateConfig(JSON.stringify(config), norm !== undefined);
  };

  const updateProperty = (property: WidgetTenantControlStatusProperties) => {
    setProperty(property);
    const config = getConfig(selectedTenants, norm?.isoNormId, property);
    props.onUpdateConfig(JSON.stringify(config), norm !== undefined);
  };

  const loadData = async () => {
    try {
      if (isLoading) return;
      setIsLoading(true);

      const config = loadConfig();

      //Offer the standards the selected CUSTOMERS run. Reading globalDataCache here would offer the partner's own
      //standards, so a consultancy that runs none itself gets an empty dropdown and can never configure the widget.
      let isoNorms: Norm[];
      if (isPartnerWidgetType(props.widget.widgetType)) {
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const customers = await apiGetTenantsForPartner(accessToken, appContext.globalDataCache);
        setPartnerCustomers(customers);
        isoNorms = getISONormsForTenants(customers, config.tenants ?? []);
      } else {
        await appContext.globalDataCache.norms.getItems();
        isoNorms = appContext.globalDataCache.norms.getItemsISO();
      }

      isoNorms.sort((a, b) => sortOnString(a.name, b.name));
      setISONorms(isoNorms);
      setNorm(isoNorms.find((n) => n.isoNormId === config.isoNormId));

      setSelectedTenants(config.tenants);
      setProperty(config.property ?? WidgetTenantControlStatusProperties.ControlStatus);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const getConfig = (
    items: string[],
    isoNormId: number | undefined,
    property: WidgetTenantControlStatusProperties,
  ): WidgetTenantControlStatusConfig => {
    const config = new WidgetTenantControlStatusConfig();
    config.tenants = items;
    config.isoNormId = isoNormId;
    config.property = property;

    return config;
  };

  const getOptions = (): IComboBoxOption[] => {
    return isoNorms.map((n) => {
      return {
        key: n.isoNormId as number,
        text: n.name,
      };
    });
  };

  const getProperties = (): IPSelectableOption[] => {
    return [
      {
        key: WidgetTenantControlStatusProperties.ControlStatus,
        text: getPropertyText(WidgetTenantControlStatusProperties.ControlStatus, t),
      },
      {
        key: WidgetTenantControlStatusProperties.ApplicabilityReason,
        text: getPropertyText(WidgetTenantControlStatusProperties.ApplicabilityReason, t),
      },
      {
        key: WidgetTenantControlStatusProperties.LinkedImplementation,
        text: getPropertyText(WidgetTenantControlStatusProperties.LinkedImplementation, t),
      },
    ];
  };

  //
  // Main render
  //
  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  return (
    <IPStack verticalFill tokens={globalStackTokensGapMedium}>
      <IPStack.Item>
        <TenantPicker
          mode={isPartnerWidgetType(props.widget.widgetType) ? TenantPickerMode.Partner : TenantPickerMode.User}
          onSelect={updateTenants}
          selectedTenants={selectedTenants}
          emptySelectionMeansAll
        />
      </IPStack.Item>
      <IPStack.Item>
        <IPLabel>{t('widgets:TenantControlStatus.Config.Label')}</IPLabel>
        <IPComboBox
          selectedKey={norm?.isoNormId}
          onChange={(ev, option) => updateNorm(option?.key as number)}
          options={getOptions()}
          useComboBoxAsMenuWidth
        ></IPComboBox>
      </IPStack.Item>
      <IPStack.Item>
        <IPDropdown
          label={t('widgets:TenantControlStatus.Config.Properties.Label')}
          selectedKey={property}
          onChange={(ev, option) => updateProperty(option?.key as number)}
          options={getProperties()}
        />
      </IPStack.Item>
    </IPStack>
  );
};
